/**
 * <VolumeLeaders> — 거래량 급증 종목 (Polygon snapshot most active)
 * 이벤트 조기 탐지 (대형 뉴스/실적/M&A 직전 거래량 급증).
 * 데이터: GET /api/ticker/market/volume-leaders → { items: [...] }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import type { WidgetMeta, WidgetProps } from "../types";
import { koreanCompanyName } from "@/lib/korean-company-name";

interface VolumeItem {
  symbol: string;
  name?: string | null;
  price: number | null;
  changePercent: number | null;
  volume: number | null;
  avgVolume: number | null;
  volumeRatio: number | null;  // volume / avgVolume
}

const REFRESH_MS = 5 * 60 * 1000;

function fmtVolume(v: number | null) {
  if (v == null) return "—";
  if (v >= 1e9) return (v / 1e9).toFixed(1) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
  return String(v);
}
function fmtPct(v: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(2)}%`;
}
function fmtPrice(v: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

export function VolumeLeaders(_props: WidgetProps) {
  const [items, setItems] = useState<VolumeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/volume-leaders", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) {
          setItems(Array.isArray(d.items) ? d.items : []);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-vl">
      <div className="dw-head"><span className="dw-title">거래량 급증</span></div>
      {error ? (
        <div className="dw-vl-error">{error}</div>
      ) : loading && items.length === 0 ? (
        <div className="dw-vl-empty">로딩 중…</div>
      ) : items.length === 0 ? (
        <div className="dw-vl-empty">데이터 없음</div>
      ) : (
        <div className="dw-vl-list">
          {items.slice(0, 6).map((it) => {
            const pct = it.changePercent;
            const up = pct != null && pct > 0;
            const down = pct != null && pct < 0;
            return (
              <Link
                key={it.symbol}
                href={`/analyze/${encodeURIComponent(it.symbol)}`}
                className="dw-vl-row"
                title={`${it.symbol} · ${koreanCompanyName(it.symbol, it.name)}`}
              >
                <span className="dw-vl-name-block">
                  <span className="dw-vl-sym">{koreanCompanyName(it.symbol, it.name)}</span>
                  <span className="dw-vl-ticker">{it.symbol}</span>
                </span>
                <span className="dw-vl-price">{fmtPrice(it.price)}</span>
                <span className={`dw-vl-pct ${up ? "up" : down ? "down" : ""}`}>{fmtPct(pct)}</span>
                <span className="dw-vl-vol">{fmtVolume(it.volume)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const volumeLeadersMeta: WidgetMeta = {
  type: "volume-leaders",
  name: "거래량 급증",
  description: "오늘 거래량 Top · 이벤트 조기 탐지",
  icon: "📊",
  category: "discovery",
  defaultSize: { w: 4, h: 5 },
  availableSizes: [
    { w: 3, h: 5 },
    { w: 4, h: 5 },
    { w: 4, h: 6 },
  ],
  minW: 3,
  maxW: 6,
  minH: 4,
  maxH: 7,
  component: VolumeLeaders,
};
